import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import useAxios from "../../utils/useAxios";
import { COUNTRY_OPTIONS, LEVEL_OPTIONS, grades, sports } from "../../constants";
import { getOptionName } from "../../utils/getOptionName";
import PlayerStatistic from "./PlayerStatistic";
import PlayerForm from "./PlayerForm";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import PhotoLightbox from "../../components/PhotoLightbox";
import Select from "react-select";
import TournamentShareModal from "../tournaments/export/TournamentShareModal";
import { useTheme } from "../../contexts/ThemeContext";
import AuthContext from "../../contexts/AuthContext";
import { cacheBustUrl } from "../../utils/cacheBustUrl";

const playerCache = {};

const PlayerProfileModal = () => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const { compactTable } = useTheme();
    const location = useLocation();
    const { user } = useContext(AuthContext);

    const query = new URLSearchParams(location.search);
    const playerId = query.get("player_id");
    const year = query.get("year") || "";
    const grade = query.get("grade") || "";
    const club = query.get("club") || "";
    const sport = query.get("sport") || "";

    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clubs, setClubs] = useState([]);
    const [activeTab, setActiveTab] = useState("statistics");
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    // Mirrors Tournament.js's own searchOpen/toggleSearch and
    // ClubProfileModal's identical copy of it - the statistics filter is
    // hidden until the header's search icon is clicked, instead of sitting
    // there permanently above the stats.
    const [searchOpen, setSearchOpen] = useState(false);
    const statsFilterRef = useRef(null);
    // Two refs, not one - the hero row's menu and the compact sticky
    // header's menu both render in the DOM at all times (CSS toggles which
    // one is interactive via opacity/pointer-events, same as the existing
    // share/x icons duplicated the same way below), so a single ref would
    // only ever point at whichever one rendered last.
    const heroMenuRef = useRef();
    const compactMenuRef = useRef();
    // Telegram-style profile: the big hero photo/info scroll away normally,
    // and the compact name+avatar in the sticky header fades in once the
    // hero has scrolled out from under it - same technique as
    // ClubProfileModal's own heroSentinelRef/isHeaderCompact.
    // initialInView:true - inView otherwise starts false until the observer's
    // first real callback lands, briefly flashing the compact avatar+name in
    // and back out on every open (see ClubProfileModal for the full story).
    const { ref: heroSentinelRef, inView: heroVisible } = useInView({ threshold: 0, initialInView: true });
    // IntersectionObserver callbacks are async (batched with the browser's
    // own compositing, not synchronous with a scrollTop write) - unlike
    // ClubProfileModal's continuous scroll-driven `progress`, a momentary
    // scrollTop reset here (whatever captureScrollBeforeSwitch below fights)
    // can get observed as a real "sentinel back in view" before the reassert
    // loop re-pins it, flashing the compact header off and back on around a
    // tab switch. forceCompact bridges exactly that window: set the instant
    // a switch starts (if the header was already compact), held for as long
    // as the reassert loop is still actively fighting, then released.
    const [forceCompact, setForceCompact] = useState(false);
    const isHeaderCompact = !heroVisible || forceCompact;
    const heroRef = useRef(null);

    const preSwitchScrollRef = useRef(null);
    const tabEffectMountedRef = useRef(false);
    const captureScrollBeforeSwitch = () => {
        const hero = heroRef.current;
        const overlay = hero?.closest(".modal-overlay");
        const content = hero?.closest(".modal-content");
        const raw = Math.max(overlay?.scrollTop || 0, content?.scrollTop || 0);
        const collapseRange = hero?.offsetHeight || 0;
        preSwitchScrollRef.current = Math.min(raw, collapseRange);
        if (isHeaderCompact) setForceCompact(true);
    };

    useLayoutEffect(() => {
        // Skip the initial mount - opening scroll position is left alone
        // (starts at 0, hero expanded); this is purely for corrections
        // after a switch away from that.
        if (!tabEffectMountedRef.current) {
            tabEffectMountedRef.current = true;
            return;
        }
        const target = preSwitchScrollRef.current;
        if (target == null) return;
        const hero = heroRef.current;
        const overlay = hero?.closest(".modal-overlay");
        const content = hero?.closest(".modal-content");
        const targets = [overlay, content].filter(Boolean);
        if (!targets.length) return;

        let cancelled = false;
        let tick = 0;
        const PIN_TICKS = 90;
        const reassert = () => {
            if (cancelled) return;
            targets.forEach(t => { t.scrollTop = target; });
            tick += 1;
            if (tick < PIN_TICKS) {
                setTimeout(reassert, 16);
            } else {
                // Reassert loop is done fighting - safe to let the real
                // IntersectionObserver reading drive isHeaderCompact again.
                setForceCompact(false);
            }
        };
        reassert();
        return () => { cancelled = true; };
    }, [activeTab]);

    useEffect(() => {
        setActiveTab("statistics");
    }, [playerId]);

    // Closes itself on a tab switch (Ratings has no filter of its own) or a
    // navigation to a different player - same reasoning as ClubProfileModal's
    // identical effect.
    useEffect(() => {
        setSearchOpen(false);
    }, [activeTab, playerId]);

    useEffect(() => {
        if (!searchOpen) return;
        // The filter doesn't exist in the DOM until this same render commits
        // it (searchOpen && (...) below) - defer past that instead of
        // racing it.
        const id = requestAnimationFrame(() => {
            statsFilterRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [searchOpen]);

    // Mirrors the real browser history entries pushed by navigateToPlayer -
    // not just a display counter. The in-modal back arrow used to run its
    // own parallel bookkeeping (a separate array, popped via history.replace
    // instead of a real back navigation), which meant the real browser Back
    // button and this arrow disagreed about where "back" even went: Back
    // would still walk through every pushed player one at a time (A, B, C
    // all real entries), while our own replace-based pop silently erased
    // that trail without the browser ever knowing. Closing afterwards left
    // A and B as ghost entries still sitting in history - pressing the
    // browser's own Back right after closing reopened one of them instead
    // of returning to wherever the user actually came from.
    const navStackRef = useRef([]);
    const [navDepth, setNavDepth] = useState(0);

    useEffect(() => {
        if (!playerId) { navStackRef.current = []; setNavDepth(0); }
    }, [playerId]);

    const toggleStatsSearch = () => setSearchOpen(prev => !prev);

    const navigateToPlayer = (newPlayerId) => {
        if (!newPlayerId || newPlayerId === playerId) return;
        navStackRef.current.push(playerId);
        setNavDepth(navStackRef.current.length);
        // Swap just player_id, not the whole query string - this modal can
        // sit open over a tournament page carrying its own params (?tab=...,
        // ?admin=1, search filters), and a bare `?player_id=...` push wiped
        // all of those, dropping the background page back to its default
        // tab the moment you clicked through to another player.
        const newQuery = new URLSearchParams(location.search);
        newQuery.set("player_id", newPlayerId);
        history.push({ search: newQuery.toString() });
    };

    // Delegates to the real history stack instead of replaying our own copy
    // of it - this is now the exact same action a hardware/software browser
    // Back press performs, so the two can never disagree about depth. The
    // history.listen effect below is what actually keeps navDepth in sync
    // once the navigation completes (from either trigger).
    const handleBack = () => history.goBack();

    useEffect(() => {
        const unlisten = history.listen((loc, action) => {
            if (action !== "POP") return;
            if (navStackRef.current.length === 0) return;
            navStackRef.current.pop();
            setNavDepth(navStackRef.current.length);
        });
        return unlisten;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentYear = new Date().getFullYear();
    const [years] = useState(
        Array.from({ length: currentYear - 2011 + 1 }, (_, i) => ({
            id: currentYear - i,
            name: String(currentYear - i)
        }))
    );

    const [activeChips, setActiveChips] = useState(() => {
        const initialChips = [];
        if (year) initialChips.push({ value: `year_${year}`, label: year, type: 'year', realValue: year });
        if (grade) {
            const obj = grades.find(g => String(g.id) === String(grade));
            if (obj) initialChips.push({ value: `grade_${grade}`, label: t(obj.name), type: 'grade', realValue: grade });
        }
        if (sport) {
            const obj = sports.find(s => String(s.id) === String(sport));
            if (obj) initialChips.push({ value: `sport_${sport}`, label: t(obj.name), type: 'sport', realValue: sport });
        }
        if (club) initialChips.push({ value: `club_${club}`, label: club, type: 'club', realValue: club });
        return initialChips;
    });

    useEffect(() => {
        if (clubs.length > 0 && club) {
            setActiveChips(prev => prev.map(chip => {
                if (chip.type === 'club' && String(chip.realValue) === String(club)) {
                    const found = clubs.find(c => String(c.id) === String(club));
                    if (found) return { ...chip, label: found.name };
                }
                return chip;
            }));
        }
    }, [clubs, club]);

    const filterOptions = useMemo(() => [
        {
            label: t('Sport type'),
            options: sports.map(s => ({ value: `sport_${s.id}`, label: t(s.name), type: 'sport', realValue: s.id }))
        },
        {
            label: t('Format'),
            options: grades.map(g => ({ value: `grade_${g.id}`, label: t(g.name), type: 'grade', realValue: g.id }))
        },
        {
            label: t('Club'),
            options: clubs.map(c => ({ value: `club_${c.id}`, label: c.name, type: 'club', realValue: c.id }))
        },
        {
            label: t('Year'),
            options: years.map(y => ({ value: `year_${y.id}`, label: y.name, type: 'year', realValue: y.id }))
        }
    ], [years, clubs, t]);

    useEffect(() => {
        if (!playerId || clubs.length > 0) return;
        const cachedClubs = sessionStorage.getItem("cached_clubs");
        if (cachedClubs) {
            setClubs(JSON.parse(cachedClubs));
        } else {
            api.get("/api/v1.0/clubs/?limit=100")
                .then(res => {
                    const fetchedClubs = res.data.results || [];
                    setClubs(fetchedClubs);
                    sessionStorage.setItem("cached_clubs", JSON.stringify(fetchedClubs));
                })
                .catch(console.error);
        }
    }, [playerId, clubs.length]);

    useEffect(() => {
        if (!playerId) {
            setPlayer(null);
            return;
        }

        if (!player) setLoading(true);

        const params = { statistic: true };
        if (year) params.year = year;
        if (grade) params.grade = grade;
        if (club) params.club = club;
        if (sport) params.sport = sport;

        api.get(`/api/v1.0/players/${playerId}/`, { params })
            .then(res => setPlayer(res.data))
            .catch(() => {
                toast.error(t("Player not found"));
                handleClose();
            })
            .finally(() => setLoading(false));
    }, [playerId, year, grade, club, sport]);

    const handleClose = () => {
        // Unwinds the whole A -> B -> C chain in one go, not just the
        // current entry - closing from C used to leave A and B as ghost
        // entries still sitting in browser history (see navStackRef's own
        // comment above), so the browser's own Back right after closing
        // reopened one of them instead of returning to wherever the user
        // actually came from. +1 covers undoing whatever originally opened
        // the first player in the chain, not just the pushes this modal
        // made on top of it.
        const depth = navStackRef.current.length;
        navStackRef.current = [];
        setNavDepth(0);
        if (depth > 0) {
            history.go(-(depth + 1));
            return;
        }
        const newQuery = new URLSearchParams(location.search);
        newQuery.delete("player_id");
        newQuery.delete("year");
        newQuery.delete("grade");
        newQuery.delete("club");
        newQuery.delete("sport");
        history.replace({ search: newQuery.toString() });
    };

    const handleFilterChange = (newValues) => {
        const newQuery = new URLSearchParams(location.search);
        ['year', 'grade', 'club', 'sport'].forEach(key => newQuery.delete(key));

        const vals = newValues || [];
        const uniqueVals = [];
        const seenTypes = new Set();
        for (let i = vals.length - 1; i >= 0; i--) {
            if (!seenTypes.has(vals[i].type)) {
                seenTypes.add(vals[i].type);
                uniqueVals.unshift(vals[i]);
            }
        }

        uniqueVals.forEach(chip => {
            newQuery.set(chip.type, chip.realValue);
        });

        setActiveChips(uniqueVals);
        history.replace({ search: newQuery.toString() });
    };

    // Same creator-or-superuser check as LocationForm/ClubForm - no
    // per-entity can_edit flag exists for players (unlike tournaments), so
    // this is the only permission signal available on both ends.
    const canManagePlayer = !!user && (user.is_superuser || (player?.creator_id && player.creator_id === user.user_id));

    useEffect(() => {
        if (!isMenuOpen) return;
        const handleClickOutside = (e) => {
            const insideHero = heroMenuRef.current && heroMenuRef.current.contains(e.target);
            const insideCompact = compactMenuRef.current && compactMenuRef.current.contains(e.target);
            if (!insideHero && !insideCompact) setIsMenuOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isMenuOpen]);

    if (!playerId || (!loading && !player)) return null;

    // Same idea as the tournament players list (TournamentPlayers.js/
    // PlayersSkeleton): render the real .profile-hero markup right away and
    // let individual cells fall back to a <Skeleton> until `player` has a
    // value, instead of swapping in a differently-shaped placeholder block
    // and replacing it wholesale once data lands - same DOM the whole time,
    // nothing for the browser to reflow when the data arrives.
    const fullName = player && <><span className={compactTable ? "" : "uppercase"}>{player.last_name}</span>, {player.first_name}</>;

    const playerFullName = player ? `${player.first_name || ""} ${player.last_name || ""}`.trim() : "";

    return (
        <>
        <CustomModal isOpen={!!playerId} onClose={handleClose} hideCloseButton noAnimation>
            <form className="modal-form entity-profile-page">
                <div ref={heroRef} className="profile-hero">
                    {/* Title + same icons/style as .profile-header-compact-share
                        below, just in normal (non-sticky) flow at the top of the
                        hero - scrolls away with it like ordinary content, rather
                        than overlaying anything. The compact header's own copy
                        takes over once that row has scrolled in. align-items:
                        center, same as ClubProfileModal/LocationProfileModal's
                        identical row - keeps the title and icons on one shared
                        line instead of the icons' own padding reading as
                        floating off it. */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", paddingTop: "10px", marginBottom: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 500, fontSize: "100%" }}>
                            {navDepth > 0 && (
                                <FeatherIcon icon="arrow-left" size={20} className="profile-header-icon-btn" onClick={handleBack} />
                            )}
                            <FeatherIcon icon="user" size={18} />
                            {t("Profile")}
                        </div>
                        {/* gap:2px, not 18px - see ClubProfileModal.js's identical
                            icon row for why (profile-header-icon-btn's own 8px
                            padding on each side already eats into the gap). */}
                        <div style={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                            {activeTab === "statistics" && (
                                <FeatherIcon icon="search" size={16} className="profile-header-icon-btn" onClick={toggleStatsSearch} />
                            )}
                            <FeatherIcon icon="share-2" size={16} className="profile-header-icon-btn" onClick={() => setIsShareOpen(true)} />
                            {/* display:flex on the wrapper below - without it this div
                                (nested one level deeper than the bare share/x icons, which
                                sit directly in the outer flex row and get blockified by it)
                                leaves its own FeatherIcon child at the SVG default of
                                display:inline, reading a couple px higher than its
                                siblings from the inline baseline gap under it. */}
                            {canManagePlayer && (
                                <div ref={heroMenuRef} style={{ position: "relative", display: "flex" }}>
                                    <FeatherIcon icon="more-vertical" size={16} className="profile-header-icon-btn" onClick={() => setIsMenuOpen(prev => !prev)} />
                                    <div className="dots_menu_container">
                                        <div className="relative">
                                            <div className={`options-menu ${isMenuOpen ? 'block_active' : 'block_inactive'}`}>
                                                <div className="options-content-block">
                                                    <a href="#" className="option pointer" style={{ border: 0, display: "block" }} onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); setIsEditOpen(true); }}>
                                                        <FeatherIcon icon="edit-2" /> {t("Edit profile")}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <FeatherIcon icon="x" size={16} className="profile-header-icon-btn" onClick={handleClose} />
                        </div>
                    </div>
                    <table style={{ width: "100%" }}>
                        <tbody>
                        <tr>
                            <td style={{ width: "110px", verticalAlign: "middle" }}>
                                {player ? (
                                    <div>
                                        {player.image
                                            ? <img
                                                  src={player.image}
                                                  className="thumbnail big avatar-zoomable"
                                                  alt="avatar"
                                                  onClick={() => setLightboxSrc(player.image)}
                                              />
                                            : <div className="no-thumbnail big"><FeatherIcon icon="user" /></div>}
                                    </div>
                                ) : (
                                    <div style={{ display: "flex" }}>
                                        <Skeleton circle width={100} height={100} />
                                    </div>
                                )}
                            </td>
                            <td style={{ verticalAlign: "top" }}>
                                <table className="player_modal" style={{ width: "100%" }}>
                                    <tbody>
                                    <tr>
                                        <td><label>{t("Name")}</label></td>
                                        <td>{player ? fullName : <Skeleton width="90%" />}</td>
                                    </tr>
                                    <tr>
                                        <td><label>{t("Resident")}</label></td>
                                        <td>
                                            {player ? player.city && (
                                                <div>
                                                    {player.city}
                                                    {player.country && `, ${t(getOptionName(COUNTRY_OPTIONS, player.country))}`}
                                                </div>
                                            ) : <Skeleton width="70%" />}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><label>{t("Level")}</label></td>
                                        <td>
                                            {player ? player.level && (
                                                <div>
                                                    {t(getOptionName(LEVEL_OPTIONS, player.level))}
                                                </div>
                                            ) : <Skeleton width="50%" />}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{border: 0}}><label>{t("Age")}</label></td>
                                        <td style={{border: 0}}>
                                            {player ? player.age && (
                                                <div>
                                                    {player.age}
                                                </div>
                                            ) : <Skeleton width="30%" />}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                {/* White background + same bleed as .profile-hero/.modal-sticky-
                    header (entity-profile-page, CustomModal.css) - this sentinel
                    sits between them with neither otherwise, showing the
                    modal's now-dark body-background through for exactly this
                    1px, left/right as well as the row itself, since a plain
                    100%-width div here sits inset within modal-content's own
                    side padding instead of reaching as far out as its bled
                    neighbors. */}
                <div ref={heroSentinelRef} className="hero-sentinel" style={{ height: "1px" }} />

                <div className={`modal-sticky-header ${isHeaderCompact ? "compact" : ""}`}>
                    <div className="profile-header-compact">
                        {navDepth > 0 && (
                            <FeatherIcon icon="arrow-left" size={20} className="profile-header-icon-btn" onClick={handleBack} />
                        )}
                        <span className="profile-header-compact-avatar">
                            {player && (player.image
                                ? <img src={player.image} alt="" className="avatar-zoomable" onClick={() => setLightboxSrc(player.image)} />
                                : <span className="no-thumbnail"><FeatherIcon icon="user" /></span>)}
                        </span>
                        <span className="profile-header-compact-titles">
                            <span className="profile-header-compact-name">{fullName}</span>
                            {player && (player.city || player.level) && (
                                <span className="profile-header-compact-subtitle">
                                    {[
                                        player.city && [player.city, player.country && t(getOptionName(COUNTRY_OPTIONS, player.country))].filter(Boolean).join(", "),
                                        player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
                                    ].filter(Boolean).join(" · ")}
                                </span>
                            )}
                        </span>
                        <span className="profile-header-compact-share" style={{ display: "flex", alignItems: "flex-start", gap: "2px" }}>
                            {activeTab === "statistics" && (
                                <FeatherIcon icon="search" size={16} className="profile-header-icon-btn" onClick={toggleStatsSearch} />
                            )}
                            <FeatherIcon icon="share-2" size={16} className="profile-header-icon-btn" onClick={() => setIsShareOpen(true)} />
                            {canManagePlayer && (
                                <div ref={compactMenuRef} style={{ position: "relative", display: "flex" }}>
                                    <FeatherIcon icon="more-vertical" size={16} className="profile-header-icon-btn" onClick={() => setIsMenuOpen(prev => !prev)} />
                                    <div className="dots_menu_container">
                                        <div className="relative">
                                            <div className={`options-menu ${isMenuOpen ? 'block_active' : 'block_inactive'}`}>
                                                <div className="options-content-block">
                                                    <a href="#" className="option pointer" style={{ border: 0, display: "block" }} onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); setIsEditOpen(true); }}>
                                                        <FeatherIcon icon="edit-2" /> {t("Edit profile")}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <FeatherIcon icon="x" size={16} className="profile-header-icon-btn" onClick={handleClose} />
                        </span>
                    </div>

                    <ul className="tournament-header-tabs" style={{ marginTop: "10px" }}>
                        <li className={activeTab === "statistics" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("statistics"); }}>{t("Statistics")}</a>
                        </li>
                        <li className={activeTab === "ratings" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("ratings"); }}>{t("Ratings")}</a>
                        </li>
                    </ul>
                </div>
                {/* Fixed min-height, not just whatever each tab's own content
                    needs - same reasoning as ClubProfileModal's own identical
                    wrapper: keeps every tab tall enough to hold whatever
                    scroll position the user was already at, so a switch to a
                    short tab (e.g. Ratings with nothing in it yet) doesn't
                    get its scrollTop clamped back down by the browser,
                    un-collapsing the header for a tab switch alone. */}
                <div style={{ minHeight: "100vh" }}>
                {player && (activeTab === "statistics" ? (
                        <div>
                            {searchOpen && (
                                <div style={{ marginBottom: '10px', marginTop: '10px' }}>
                                    <div className="tournament-search-container">
                                        <FeatherIcon icon="filter" size={16} className="tournament-search-icon" />
                                        <Select
                                            ref={statsFilterRef}
                                            isMulti
                                            options={filterOptions}
                                            value={activeChips}
                                            onChange={handleFilterChange}
                                            placeholder={t("Filter statistics...")}
                                            classNamePrefix="react-select"
                                            noOptionsMessage={() => t("No matching options")}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '20px', padding: '15px', background: 'var(--body-background)', borderRadius: '10px', marginBottom: "10px" }}>
                                <div style={{ color: 'var(--easy-black)' }}>{t("Matches")}: {player.statistic?.total_matches || 0}</div>
                                <div style={{ color: 'var(--primary-green)' }}>{t("Win")}: {player.statistic?.wins || 0}{player.statistic?.win_percentage && ` (${player.statistic.win_percentage}%)`}</div>
                                <div style={{ color: 'var(--secondary-color)' }}>{t("Lose")}: {player.statistic?.losses || 0}{player.statistic?.loss_percentage && ` (${player.statistic.loss_percentage}%)`}</div>
                            </div>
                            <PlayerStatistic
                                playerId={playerId}
                                year={year}
                                grade={grade}
                                club={club}
                                sport={sport}
                                onPlayerClick={navigateToPlayer}
                            />
                        </div>
                    ) : (
                        <div style={{ padding: "30px 15px", textAlign: "center", color: "var(--secondary-color)" }}>
                            {t("No ratings yet")}
                        </div>
                    ))}
                </div>
            </form>
        </CustomModal>
        <TournamentShareModal
            isOpen={isShareOpen}
            onClose={() => setIsShareOpen(false)}
            url={window.location.href}
            title={playerFullName}
            shareText={playerFullName ? t("Check out the player {{name}}!", { name: playerFullName }) : undefined}
            linkLabel={t("Player link")}
            qrCaption={t("Scan with camera to open the player's profile")}
        />
        <PhotoLightbox src={lightboxSrc} alt={playerFullName} onClose={() => setLightboxSrc(null)} />
        <CustomModal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)}>
            <PlayerForm
                playerId={playerId}
                onClose={() => setIsEditOpen(false)}
                onSuccess={(newItem) => {
                    if (!newItem?.id) {
                        // Deleted - nothing left here to show.
                        setIsEditOpen(false);
                        handleClose();
                        return;
                    }
                    // Same cache-bust reasoning as PlayersList.js's own
                    // onSuccess - a replaced photo keeps its filename and
                    // /media/ is cached for 30 days.
                    setPlayer(prev => ({
                        ...prev,
                        ...newItem,
                        image: newItem.image ? cacheBustUrl(newItem.image) : newItem.image,
                    }));
                }}
                img={player?.image || null}
                width={player?.width || null}
                height={player?.height || null}
                canWrite={canManagePlayer}
                hideDelete={!canManagePlayer}
            />
        </CustomModal>
        </>
    );
};

export default PlayerProfileModal;
